import { ClassifiedObjectType } from '@atlas/types'
import { useCallback, useState } from 'react'
import { apiErrorMessage } from '@/lib/api-error'
import { logger } from '@/lib/logger'
import { notification } from '@/notifications/notification-store'
import { type ClassifyTuple, classificationApi } from '@/services/classification.service'
import { enqueueClassified } from '@/upload/enqueue-with-tuples'
import { getItems, setAttributeFailures } from '@/upload/upload-store'
import type { StagedFile } from './useStagedFiles'

/** What a commit did, so the page can clear what landed and keep what didn't. */
export interface CommitOutcome {
	uploadedIds: string[]
	errors: Record<string, string>
}

/** Tag so this page's tiles are distinguishable in the global upload store. */
export const UPLOAD_CLASSIFY_OWNER = 'upload-classify'

const log = logger.child('upload-classify')

/**
 * Writes a file's resolved attribute values once its classification concern is
 * terminal, one `setAttribute` call per axis. A rejected call is caught
 * per-axis — so one failing attribute never stops the rest from being tried —
 * and every failing axis code is recorded on the upload item, surviving after
 * the one-shot notification (thrown once, below) has been dismissed. A clean
 * run clears any badge left by an earlier attempt, so the marker always
 * describes the latest one.
 */
async function applyAttributes(fileId: string, entries: { axisCode: string; valueCode: string }[]): Promise<void> {
	const failedAxisCodes: string[] = []
	await Promise.all(
		entries.map(async ({ axisCode, valueCode }) => {
			try {
				await classificationApi.setAttribute(ClassifiedObjectType.FILE, fileId, axisCode, [valueCode])
			} catch {
				failedAxisCodes.push(axisCode)
			}
		}),
	)
	const item = getItems().find((candidate) => candidate.fileId === fileId)
	if (failedAxisCodes.length === 0) {
		// Cleared, not just left unset: an item is retried in place, so a badge from
		// the previous attempt would outlive the failure it described.
		if (item?.attributeFailures?.length) setAttributeFailures(item.id, [])
		return
	}
	if (item) {
		setAttributeFailures(item.id, failedAxisCodes)
	} else {
		// The badge is the only record that survives the one-shot notification, so
		// losing the item is the failure becoming invisible — say so rather than
		// no-op silently.
		log.warn('attribute write failed but no upload item matched the file; badge not recorded', {
			fileId,
			failedAxisCodes,
		})
	}
	throw new Error(`Failed to save ${failedAxisCodes.length} attribute value(s)`)
}

/**
 * Uploads a classified batch, one file at a time, and reports which succeeded.
 *
 * Each file's tuples ride its own `confirmUpload`, so the server applies them as
 * a tracked `classification` concern — the client never calls `/classify/batch`
 * here. That is what lets a concern like RAG ingestion declare
 * `dependsOn: ['classification']` and actually be gated by it: the tuples exist
 * before `FILE_UPLOADED` is emitted.
 *
 * A failure is per-file, never per-batch. The files that uploaded are reported
 * so the page can drop them; the rest keep their error and stay in the table, so
 * pressing Commit again retries only what didn't land.
 *
 * Attribute values are written separately, through `setAttribute` — never via
 * `classifyBatch` — once each file's `classification` concern is terminal,
 * mirroring `useWorkOrderFiles.ts`'s `useUploadDeliverable` precedent. That
 * write runs regardless of whether classification succeeded, failed, or was
 * skipped, and a failure there never fails or rolls back the upload itself.
 */
export function useCommitUploads() {
	const [isCommitting, setIsCommitting] = useState(false)

	const commit = useCallback(
		async (
			entries: {
				staged: StagedFile
				tuples: (fileId: string) => ClassifyTuple[]
				/** Zero-arg: unlike a tuple, an attribute entry does not name the file it belongs to. */
				attributes: () => { axisCode: string; valueCode: string }[]
			}[],
		): Promise<CommitOutcome> => {
			setIsCommitting(true)
			const uploadedIds: string[] = []
			const errors: Record<string, string> = {}

			// All enqueued at once so every file gets its tile immediately; the store
			// caps how many PUTs actually run in parallel, so this changes visibility
			// and not throughput. Each result is recorded against its own file, so a
			// partial failure is still per-file rather than per-batch.
			await Promise.all(
				entries.map(async (entry) => {
					try {
						await enqueueClassified(entry.staged.file, entry.tuples, {
							owner: UPLOAD_CLASSIFY_OWNER,
							afterClassified: (file) => applyAttributes(file.fileId, entry.attributes()),
							// The upload and classification already succeeded — a failed
							// attribute write is reported, not fatal.
							onFollowUpError: (error) =>
								notification.error(apiErrorMessage(error, 'The file uploaded, but saving some attributes failed.')),
						})
						uploadedIds.push(entry.staged.stagedId)
					} catch (error) {
						errors[entry.staged.stagedId] = apiErrorMessage(error, 'Upload failed')
					}
				}),
			)

			setIsCommitting(false)
			return { uploadedIds, errors }
		},
		[],
	)

	return { commit, isCommitting }
}
